/* eslint-disable @typescript-eslint/no-shadow */
import { useGlobalStore } from "@/libs/store/globalStore/useGlobalStore";
import type { CurrencyDataInterface } from "@/types/utils/currency";
import { currencyData } from "@/types/utils/currency";
import { numberToLocaleString } from "@/utils/data/priceFormatter";
import { CookiesItemEnum } from "@/utils/enums/CookiesItemEnum";
import { UtmChanelEnum } from "@/utils/enums/UtmChanelEnum";

import { getCookies } from "../cookiesHelper/CookiesUtills";

export interface FormatCurrencyOptions {
  isDesktop?: boolean;
  isShowDecimal?: boolean;
  isMathFloor?: boolean;
  isShowZeroPrice?: boolean;
}

export interface FormatCurrencyNumberOptions {
  isShowDecimal?: boolean;
  isMathFloor?: boolean;
  isAllowZero?: boolean;
}
// format price currency
// isDesktop >>> 1,250 THB, !isDesktop >>> ฿ 1,250
export const formatCurrencyNumber = (
  value: number | undefined,
  options?: FormatCurrencyNumberOptions,
) => {
  const isShowDecimal = options?.isShowDecimal ?? true;
  const isMathFloor = options?.isMathFloor ?? false;
  const isAllowZero = options?.isAllowZero ?? false;

  const formattedValue = (
    isMathFloor ? Math.floor(value ?? 0) : (value ?? 0)
  ).toLocaleString(undefined, {
    minimumFractionDigits: isShowDecimal ? 2 : 0,
    maximumFractionDigits: isShowDecimal ? 2 : 0,
  });
  if (!isAllowZero && formattedValue === "0.00") {
    return "";
  }
  return formattedValue;
};

export function formatCurrency(
  value: number | undefined,
  currencyStatic: string,
  options?: FormatCurrencyOptions,
): string {
  const utmId = getCookies(CookiesItemEnum.COOKIE_UTM_ID);

  const isDesktop = options?.isDesktop ?? true;
  const isShowDecimal = options?.isShowDecimal ?? false;
  const isMathFloor = options?.isMathFloor ?? true;
  const isShowZeroPrice = options?.isShowZeroPrice ?? false;
  if (value === undefined) return `0 ${currencyStatic}`;
  const currency: CurrencyDataInterface = currencyData.find(
    (data) => data.currency === currencyStatic,
  ) ?? {
    currency: currencyStatic,
    decimalDigits: isShowDecimal ? 2 : 0,
    sortDecimalDigits: currencyStatic,
    labelEN: currencyStatic,
    labelTH: currencyStatic,
  };

  const formattedValue = numberToLocaleString(
    isMathFloor ? Math.floor(value) : value,
    {
      minimumFractionDigits: isShowDecimal ? currency?.decimalDigits : 0,
      maximumFractionDigits: isShowDecimal ? currency?.decimalDigits : 0,
    },
  );

  if (!isShowZeroPrice && value === 0) {
    return "";
  }
  if (!isDesktop && utmId !== UtmChanelEnum.GOOGLE) {
    const currencyStaticMobile = currency?.sortDecimalDigits;
    if (currencyStaticMobile === undefined) {
      return `${formattedValue}`;
    }
    return `${currencyStaticMobile} ${formattedValue}`;
  }

  return `${formattedValue} ${currencyStatic}`;
}

export const formatCurrencyPayment = (
  value: number | undefined,
  currencyStatic: string,
  options?: FormatCurrencyOptions,
): string => {
  const isDesktop = options?.isDesktop ?? true;
  const isShowDecimal = options?.isShowDecimal ?? true;
  const isMathFloor = options?.isMathFloor ?? false;
  const isShowZeroPrice = options?.isShowZeroPrice ?? true;
  return formatCurrency(value, currencyStatic, {
    isDesktop,
    isShowDecimal,
    isMathFloor,
    isShowZeroPrice,
  });
};

export const formatCurrencyWithZeroReturn = (
  value: number | undefined,
  currencyStatic: string,
  options?: FormatCurrencyOptions,
): string => {
  if (value === undefined) return `0 ${currencyStatic}`;
  return formatCurrency(value, currencyStatic, options);
};

export function getAndFormatCurrencyCode(
  currencyCode: string,
): string | undefined {
  const currency = currencyData.find((data) => data.currency === currencyCode);
  const { isDesktop } = useGlobalStore();

  if (isDesktop) return currencyCode;
  return currency?.sortDecimalDigits;
}
